// Page-wide zoom for the dense table screens (InvoicingPage, QuotationPage,
// TimeEntriesListPage).
//
// Each renders as if the browser were at 80%, so more rows and columns fit. CSS zoom
// rather than rescaling each size by hand: the grid cells alone carry dozens of
// inline rem font sizes. It is applied in three places, because two of the things
// on screen sit outside a page's root element:
//   - the page root: `zoom: PAGE_ZOOM` plus `height: calc(100vh / PAGE_ZOOM)`, since
//     zoom scales vh too and a plain 100vh would leave the bottom fifth empty;
//   - Dialog paper, which portals to <body>: the withPageZoom nested theme
//     (DocumentViewerDialog opts back out: an iframed PDF viewer under zoom
//     cannot scroll to the bottom of the document);
//   - the app-level Header: App.js passes PAGE_ZOOM on every desktop route, so the
//     header is one size app-wide; the mobile top bar only while isZoomedPagePath matches.
// Menus, Select/Autocomplete lists and Tooltips are deliberately left at full size:
// they are positioned from the anchor's on-screen rect, and zooming the popup itself
// would shift it off that anchor. DataGrid v6 is safe under zoom: it sizes itself
// from clientWidth/clientHeight/getComputedStyle, which stay in its own (unzoomed)
// coordinates, and never from getBoundingClientRect.

export const PAGE_ZOOM = 0.8;

// The routes whose page applies PAGE_ZOOM, so the Header can match it.
const ZOOMED_PAGE_PATH = /^\/[^/]+\/(invoicing|quotation|time_entries)\/?$/;
// Pages that drop the zoom on phones (below sm): all three — Time Entries since
// 2026-10-01, Invoicing and Quotation since 2026-10-20. On phones they use
// withPageTabUnderline instead of withPageZoom.
const PHONE_UNZOOMED_PAGE_PATH = /^\/[^/]+\/(invoicing|quotation|time_entries)\/?$/;
export const isPhoneUnzoomedPath = (pathname) => PHONE_UNZOOMED_PAGE_PATH.test(pathname || '');
export const isZoomedPagePath =  (pathname) => ZOOMED_PAGE_PATH.test(pathname);

// Adds `extra` to one styleOverrides slot of a theme component, keeping the slot's
// existing override. theme.js writes those as callbacks, which deepmerge would
// replace outright rather than merge.
const extendSlot = (components, name, slot, extra) => {
  const comp = components?.[name] ?? {};
  const base = comp.styleOverrides?.[slot];
  return {
    ...comp,
    styleOverrides: {
      ...comp.styleOverrides,
      [slot]: (props) => ({ ...(typeof base === 'function' ? base(props) : base), ...extra }),
    },
  };
};

// Nested-theme function for a zoomed page's <ThemeProvider>, covering what the zoom breaks:
// - Dialog paper gets the zoom itself, since dialogs portal out of the zoomed root.
// - Tabs drop MUI's sliding indicator for an underline drawn on the selected tab
//   (withPageTabUnderline below).
export const withPageZoom = (outer) => {
  const tabbed = withPageTabUnderline(outer);
  return {
    ...tabbed,
    components: {
      ...tabbed.components,
      MuiDialog: extendSlot(outer.components, 'MuiDialog', 'paper', { zoom: PAGE_ZOOM }),
    },
  };
};

// Only the Tabs half of withPageZoom, for Invoicing / Quotation on phones, where the
// page is not zoomed: the tabs keep the same drawn underline as on desktop (the
// main tab strips draw their own inset shadow too, so MUI's indicator would double it).
// MUI places the indicator from getBoundingClientRect, which already reports
// zoomed (on-screen) values, then writes them into a left/width that is zoomed
// again, so under zoom the bar lands short of and to the left of its tab. An
// inset box-shadow on the tab needs no measuring. currentColor matches the
// indicator it replaces: both follow the Tabs' textColor/indicatorColor.
export const withPageTabUnderline = (outer) => ({
  ...outer,
  components: {
    ...outer.components,
    MuiTabs: extendSlot(outer.components, 'MuiTabs', 'indicator', { display: 'none' }),
    MuiTab: extendSlot(outer.components, 'MuiTab', 'root', {
      '&.Mui-selected': { boxShadow: 'inset 0 -2px 0 currentColor' },
    }),
  },
});
